import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import BrushIcon from '../../../assets/svg/brush.svg?react';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';

import CircularProgress from '@mui/material/CircularProgress';
import { useQualificationsCardForm } from './useQualificationsCardForm';
import styles from './QualificationsCard.module.scss';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import Tooltip from '@mui/material/Tooltip';
// import { SocialLink } from '../social-media-form/SocialMediaForm';
import { Employee } from '../../models/employee.model';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { USER_PERMISSIONS } from '../constants/permissions';

type QualificationsCardProps = {
  simplified?: boolean;
  employee: Partial<Employee>;
};

export default function QualificationsCard({ employee }: QualificationsCardProps) {
  const { t } = useTranslation();
  const isLoading = false;
  const {
    formData,
    handleChange,
    saveChanges,
    isEdited,
    toggleEditing,
    formRef,
    showModal,
    confirmExit,
    cancelChanges,
  } = useQualificationsCardForm(employee);

  const checkPermission = useCheckPermission();

  return (
    <Grid container className='inputs-editable'>
      <Grid size={{ xs: 12 }}>
        <Card className={`${styles.card} ${isEdited ? styles.card_border : ''}`} ref={formRef}>
          <CardContent className={isLoading ? styles.card_content_min_height : ''}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                <Grid container alignItems='center' flexWrap='nowrap' p={1}>
                  <Grid size={{ xs: 11 }} container alignItems='center' gap={1}>
                    <Grid sx={{ marginBottom: '0.2rem' }}>
                      <SchoolOutlinedIcon />
                    </Grid>
                    <Grid>
                      <Typography variant='h6'>Qualifications</Typography>{' '}
                      {/*TODO : Add translation */}
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 1 }} container justifyContent='flex-end'>
                    {!isEdited ? (
                      <Tooltip
                        className='flex-shrink-0'
                        title={
                          checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE)
                            ? t('buttons.edit')
                            : t('messages.contactAdministrator')
                        }
                        placement='top'
                      >
                        <IconButton
                          onClick={toggleEditing}
                          disabled={!checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE)}
                        >
                          <BrushIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Box display='flex' gap='8px' alignItems='center'>
                        <Button
                          type='button'
                          className='btn-default btn-action-basic btn-cancel'
                          role='button'
                          variant='contained'
                          size='large'
                          onClick={cancelChanges}
                        >
                          {t('buttons.cancel')}
                        </Button>
                        <Button
                          type='submit'
                          className='btn-default btn-action-basic'
                          role='button'
                          disabled={!isEdited}
                          variant='contained'
                          size='large'
                          onClick={saveChanges}
                        >
                          {t('buttons.save')}
                        </Button>
                      </Box>
                    )}
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <TextField
                          fullWidth
                          value={formData.degree ?? ''}
                          label={t('labels.degreeOrCertificate')}
                          onChange={(e) => {
                            handleChange('degree')(e.target.value);
                          }}
                          disabled={!isEdited}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        {' '}
                        <TextField
                          fullWidth
                          value={formData.university_name ?? ''}
                          label={t('labels.institutionName')}
                          onChange={(e) => {
                            handleChange('university_name')(e.target.value);
                          }}
                          disabled={!isEdited}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 6 }}>
                            {' '}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label={t('labels.startDate')}
                                value={formData.start_date ? dayjs(formData.start_date) : null}
                                onChange={(newValue) => {
                                  const formattedDate = newValue
                                    ? newValue.format('YYYY-MM-DD')
                                    : null;
                                  handleChange('start_date')(formattedDate);
                                }}
                                disabled={!isEdited}
                                format='DD/MM/YYYY'
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    placeholder: 'DD/MM/YYYY',
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            {' '}
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label={t('labels.endDate')}
                                value={formData.end_date ? dayjs(formData.end_date) : null}
                                onChange={(newValue) => {
                                  // Format date as YYYY-MM-DD for the form data
                                  const formattedDate = newValue
                                    ? newValue.format('YYYY-MM-DD')
                                    : null;
                                  handleChange('end_date')(formattedDate);
                                }}
                                disabled={!isEdited}
                                format='DD/MM/YYYY'
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    placeholder: 'DD/MM/YYYY',
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {
                  <Grid container className={styles.container_margin_top} alignItems='center'>
                    <Grid>
                      {/* <Tooltip title={t('businessProfile.addSocials')} placement='top'>
                        <IconButton
                          onClick={toogleEditingSocialModal}
                          className={styles.add_social_button}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Grid>
                  </Grid>
                }
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
      {/* <AddSocialsModal
        open={showAddSocialModal}
        onClose={saveChangesSocialForm}
        onConfirm={toogleEditingSocialModal}
      /> */}
    </Grid>
  );
}
